import type { RecordSearchParams, RecordSearchResult } from '@/components/ui/SelectRecord'

/**
 * Adapters between the SelectRecord table contract and the /v2/cpg/* record-query wire
 * shape (middleware's record-query.contract.ts) — the two are designed 1:1, so this is
 * mostly a pass-through that drops filters/sorts on columns the endpoint doesn't index
 * (e.g. the client-rendered families chips column).
 */

export type RecordQueryWire<F extends string> = {
  page: { offset: number; count: number }
  filters: { field: F; operator: 'contains' | 'eq' | 'neq' | 'gt' | 'lt'; operand: string }[]
  order?: { field: F; dir: 'asc' | 'desc' }
}

export function toRecordQuery<T, F extends string>(
  params: RecordSearchParams<T>,
  fields: readonly F[],
): RecordQueryWire<F> {
  const allowed = new Set<string>(fields)
  const filters = params.filters
    .filter((f) => allowed.has(String(f.field)))
    .map((f) => ({ field: String(f.field) as F, operator: f.operator, operand: f.operand }))
  const sortKey =
    params.sortKey !== null && allowed.has(String(params.sortKey))
      ? (String(params.sortKey) as F)
      : null
  return {
    page: { offset: params.offset, count: params.limit },
    filters,
    order: sortKey ? { field: sortKey, dir: params.sortDir === 1 ? 'asc' : 'desc' } : undefined,
  }
}

/**
 * `{ page, data }` → table page. The endpoints report `more` but no total, so the footer
 * count is "rows loaded so far", rendered with a trailing '+' while more pages exist.
 */
export function toSearchResult<R>(res: {
  page: { offset: number; size: number; more: boolean }
  data: R[]
}): RecordSearchResult<R> {
  return {
    rows: res.data,
    total: res.page.offset + res.page.size,
    hasMore: res.page.more,
    incompleteTotal: res.page.more,
  }
}
